import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "./button";
import "../scss/hero-section.scss";
import { useTranslation } from "react-i18next";
import { getItem, getLocalizedPathFromLocalStorage } from "../helper/localstorage.helper";
import { useNavigate } from 'react-router-dom';
import { features, heroCards, bussinessfeatures } from "../constant/features.constant";
import { externalLink, rightArrow } from "../components/images/svg/icons";
import { officeImage, country } from "../components/images/png/icons";

interface HeroSectionDesktopProps {
  userType: string;
  user: string | null;
}

export const HeroSectionDesktop: React.FC<HeroSectionDesktopProps> = React.memo(({ userType, user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const freelancer = "freelancer";

  const currentFeatures = React.useMemo(
    () => (userType === freelancer ? features : bussinessfeatures),
    [userType, freelancer]
  );

  return (
    <Container>
      <Row className="px-sm-0 px-0 padding-116 d-flex justify-content-end row-gap">
        {/* LEFT COLUMN */}
        <Col xs={12} lg={12} xl={6} className="px-0">
          <div className="py-custom py-custom-xl d-flex flex-column h-100">
            <div className="hero-heading-wrapper">
              <h1 className={`mb-2 Heading_h1 ${userType === freelancer ? 'width-80' : 'width-75'}`}>
                {userType === freelancer ? (
                  <>
                    {t("INVOICE_HEADING")} <span className="nowrap-heading">{t("INVOICE_HEADING_1")}</span> {t("INVOICE_HEADING_2")}
                  </>
                ) : t("INVOICE_HEADING_BUSINESS")}
              </h1>
            </div>
            <h6 className={`mb-0 py-4 margin-right ${userType === freelancer ? 'pr-0' : 'width-85'}`}>
              {t(userType === freelancer ? "INVOICE_SUB_HEADING" : "INVOICE_SUB_HEADING_BUSINESS")}
            </h6>
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mt-2">
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center justify-content-center gap-2 fs-6"
                onClick={() =>
                  navigate(
                    getLocalizedPathFromLocalStorage(
                      { get: (key: string) => getItem(key) ?? null },
                      { 'sv-se': 'e-postregister', 'en-us': 'emailregister' }
                    )
                  )
                }
              >
                {user ? t("GO_TO_DASHBOARD") : t("GET_STARTED")}
                <img src={rightArrow} className="ms-1 mb-1" alt="" style={{ marginLeft: '6px' }} width="16" height="16" loading="eager" />
              </Button>
              <div className="trustpilot-widget mt-3" data-locale="sv-SE" data-template-id="5419b732fbfb950b10de65e5" data-businessunit-id="64f712e3dbd20bbfcf3a825f" data-style-height="24px" data-style-width="100%">
                <a href="https://se.trustpilot.com/review/invozio.com" target="_blank" rel="noopener">Trustpilot</a>
              </div>
            </div>
            <Row className="">
              {currentFeatures.map((feature: { icon: string; title: string; description: string }, index: number) => (
                <Col xs={12} sm={6} md={4} className="px-0 margin-top-goals" key={index}>
                  <div className="d-flex flex-column gap-2 pr-3">
                    <img
                      src={feature.icon}
                      alt={feature.title}
                      className="feature-icon"
                      width="48"
                      height="48"
                      loading="lazy"
                    />
                    <h3 className="Heading_h5 py-2">{t(feature.title)}</h3>
                    <span className="Heading_h6 mr-1">{t(feature.description)}</span>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Col>

        {/* RIGHT COLUMN */}
        <Col xs={12} lg={12} xl={6} className="px-0">
          <Row className="d-flex justify-content-end align-items-stretch hero-cards-row">
            {heroCards.map((card, idx) => {
              let cardImg = card.img;
              let cardAlt = card.alt;
              if (userType === "business") {
                if (card.type === "laptop") {
                  cardImg = country;
                  cardAlt = "Office";
                } else if (card.type === "phone") {
                  cardImg = officeImage;
                  cardAlt = "Country";
                }
              }
              return (
                <Col
                  xs={6}
                  className={`d-flex padding-bottom-20 ${idx === 2 || idx === 3 ? 'pr-0' : 'pl-0'}`}
                  key={idx}
                >
                  <div className="card-wrapper w-100 h-100">
                    {card.content ? (
                      card.type === "currencies" ? (
                        <div className="card-content">
                          <div>
                            <span className="font-64">{card.content.number} </span> <br />
                            {t(card.content.label)}
                            <img src={card.content.img} alt={t(card.content.label)} className={card.content.imgClass} width="120" height="40" loading="lazy" />
                          </div>
                        </div>
                      ) : card.type === "insurance" ? (
                        <div className="card-content insurance">
                          <div className="d-flex align-items-center justify-content-end">
                            <img src={card.content.img} alt={t(card.content.label)} className={card.content.imgClass} width="80" height="80" loading="lazy" />
                          </div>
                          <div className={card.content.labelClass}>{t(card.content.label)}</div>
                        </div>
                      ) : null
                    ) : (
                      <div className="card-image-container">
                        <img 
                          src={cardImg} 
                          alt={cardAlt} 
                          className="card-image" 
                          width="350" 
                          height="450"
                          fetchPriority={idx < 2 ? "high" : "low"}
                          loading={idx < 2 ? "eager" : "lazy"}
                        />
                        <div className="card-overlay">
                          <span>
                            {idx === 1 ? (
                              <>
                                {userType === 'freelancer' ? (
                                  <>
                                    {t('INVOICE_WITH')}<br />
                                    ROT & RUT
                                  </>
                                ) : (
                                  <>{t('PAID_WITHIN')}<br /> 1-24 {t('HOURS_FOR_BUSSINESS_SECTION')}</>
                                )}
                              </>
                            ) : (
                              userType === 'freelancer' ? (
                                t('EXPRESS_PAYMENT_IN_24_HOUR')
                              ) : (
                                t('SEND_COLLECTIVE_INVOICE')
                              )
                            )}
                          </span>
                          <span className="overlay-icon">
                            <button onClick={() =>
                              navigate(
                                getLocalizedPathFromLocalStorage(
                                  { get: (key: string) => getItem(key) ?? null },
                                  { 'sv-se': 'faq', 'en-us': 'faq' }
                                )
                              )
                            }>
                              <img src={externalLink} alt="External Link" className="icon-width" width="24" height="24" loading="lazy" />
                            </button>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </Container>
  );
});
